import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

type UserRole = 'superuser' | 'admin' | 'user' | 'client_admin' | 'client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperuser?: boolean;
  allowedRoles?: UserRole[];  // Specify which roles can access this route
  denyRoles?: UserRole[];     // Specify which roles are denied access
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSuperuser = false,
  allowedRoles,
  denyRoles
}) => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-surface-elevated)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '2rem',
              color: 'var(--color-primary-500)'
            }}
          ></i>
          <span style={{
            color: 'var(--color-text-tertiary)',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            {t('common.loading')}
          </span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check superuser requirement
  if (requireSuperuser && user.role !== 'superuser') {
    return <AccessDenied message={t('errors.adminRequired')} />;
  }

  // Check allowed roles (whitelist)
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role as UserRole)) {
      return <AccessDenied message={t('errors.noPermission')} />;
    }
  }

  // Check denied roles (blacklist)
  if (denyRoles && denyRoles.length > 0) {
    if (denyRoles.includes(user.role as UserRole)) {
      return <AccessDenied message={t('errors.noPermission')} />;
    }
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

// Access Denied component
const AccessDenied: React.FC<{ message: string }> = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: 'var(--color-surface-elevated)',
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: 'var(--color-card-background)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: '12px',
        padding: '3rem',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        boxShadow: 'var(--color-card-shadow)'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'var(--color-tint-red)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem auto'
        }}>
          <i
            className="fas fa-lock"
            style={{
              fontSize: '1.5rem',
              color: 'var(--color-critical)'
            }}
          ></i>
        </div>
        <h2 style={{
          margin: '0 0 1rem 0',
          fontSize: '1.5rem',
          fontWeight: '700',
          color: 'var(--color-text-primary)'
        }}>
          {t('errors.accessDenied')}
        </h2>
        <p style={{
          color: 'var(--color-text-tertiary)',
          margin: '0 0 2rem 0',
          fontSize: '1rem',
          lineHeight: '1.5'
        }}>
          {message}
        </p>
        <button
          onClick={() => window.history.back()}
          style={{
            backgroundColor: 'var(--color-primary-500)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem 1.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-primary-500)';
          }}
        >
          <i className="fas fa-arrow-left"></i>
          {t('common.back', 'Zurück')}
        </button>
      </div>
    </div>
  );
};

export default ProtectedRoute;